/**
 * Press bar — swipeable ticker.
 *
 * Without JS the bar is a CSS transform animation inside overflow:hidden, which
 * cannot be grabbed. This upgrades it to a real horizontal scroll container
 * that still advances on its own, so a touch gets native scrolling and
 * momentum, and the ticker picks up again once the finger lets go.
 *
 * The CSS animation stays as the no-JS fallback; adding .press-bar--swipe
 * switches it off and hands motion over to this file.
 */
(function () {
  var RESUME_DELAY = 1400; // ms of stillness before the ticker starts again
  var reduced =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setupViewport(bar, viewport) {
    var track = viewport.querySelector('.press-bar__track');
    if (!track) return;

    var copies =
      parseFloat(getComputedStyle(bar).getPropertyValue('--press-copies')) || 3;
    var duration =
      parseFloat(getComputedStyle(bar).getPropertyValue('--press-duration')) || 40;
    var backwards = bar.classList.contains('press-bar--right');
    var pausableOnHover = bar.classList.contains('press-bar--pausable');

    var groupWidth = 0;
    var paused = false;
    var resumeTimer = null;
    var visible = true;
    var last = 0;
    var carry = 0; // sub-pixel remainder, so slow speeds still move

    function measure() {
      groupWidth = track.scrollWidth / copies;
      // Park on the middle copy so there is room to swipe either way.
      if (groupWidth > 0 && viewport.scrollLeft < 1) {
        viewport.scrollLeft = groupWidth;
      }
    }

    // The content repeats every groupWidth, so shifting by exactly one group
    // is invisible — that is what makes the loop seamless in both directions.
    function wrap() {
      if (!groupWidth) return;
      if (viewport.scrollLeft >= groupWidth * 2) {
        viewport.scrollLeft -= groupWidth;
      } else if (viewport.scrollLeft < groupWidth * 0.5) {
        viewport.scrollLeft += groupWidth;
      }
    }

    function hold() {
      paused = true;
      if (resumeTimer) clearTimeout(resumeTimer);
    }

    function release() {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        paused = false;
      }, RESUME_DELAY);
    }

    function frame(now) {
      var dt = last ? (now - last) / 1000 : 0;
      last = now;

      if (!paused && !reduced && visible && groupWidth > 0 && dt > 0 && dt < 0.5) {
        var step = (groupWidth / duration) * dt + carry;
        var whole = Math.trunc(step);
        carry = step - whole;
        if (whole) {
          viewport.scrollLeft += backwards ? -whole : whole;
          wrap();
        }
      }

      requestAnimationFrame(frame);
    }

    // A touch, a drag or a wheel means the visitor is driving: get out of the
    // way, and only take over again once they have been still for a moment.
    ['touchstart', 'pointerdown', 'wheel'].forEach(function (evt) {
      viewport.addEventListener(evt, hold, { passive: true });
    });
    ['touchend', 'touchcancel', 'pointerup', 'pointercancel'].forEach(function (evt) {
      viewport.addEventListener(evt, function () {
        wrap();
        release();
      }, { passive: true });
    });
    viewport.addEventListener('wheel', release, { passive: true });

    if (pausableOnHover) {
      viewport.addEventListener('mouseenter', hold);
      viewport.addEventListener('mouseleave', release);
      viewport.addEventListener('focusin', hold);
      viewport.addEventListener('focusout', release);
    }

    // Don't burn frames on a bar that is off screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }).observe(bar);
    }

    measure();
    // Logos are lazy-loaded, so the track keeps growing for a moment.
    if ('ResizeObserver' in window) {
      new ResizeObserver(measure).observe(track);
    } else {
      window.addEventListener('load', measure);
      window.addEventListener('resize', measure);
    }

    requestAnimationFrame(frame);
  }

  function init(bar) {
    if (bar.dataset.pressBarReady) return;
    bar.dataset.pressBarReady = '1';
    bar.classList.add('press-bar--swipe');

    var viewports = bar.querySelectorAll('.press-bar__viewport');
    for (var i = 0; i < viewports.length; i++) {
      setupViewport(bar, viewports[i]);
    }
  }

  function initAll() {
    var bars = document.querySelectorAll('.press-bar');
    for (var i = 0; i < bars.length; i++) init(bars[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders the section on every setting change.
  document.addEventListener('shopify:section:load', function (e) {
    var bar = e.target.querySelector('.press-bar');
    if (bar) init(bar);
  });
})();
